import React, { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import PostApi from 'api/post';
import { AttachmentConvertor } from 'utils/attachments';
import { AttachmentCard } from 'types/Attachments';
import { prependHttp } from 'utils/url';
import { Button, Field, Icon, IconButton, TextInput } from '@kontentino/ui';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

type Props = {
  onLinkFetched(link: AttachmentCard): void;
  onLinkRemove?(): void;
  link?: string;
  pageId: number;
  dataNamePrefix: string;
};

type FormState = z.infer<typeof schema>;

const schema = z.object({
  url: z.string().url(),
});

const AttachLink: FC<Props> = (props) => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm<FormState>({
    defaultValues: { url: props.link ?? '' },
    resolver: zodResolver(schema),
  });
  const linkInfo = useMutation(PostApi.fetchLinkInfo, {
    onSuccess(link) {
      props.onLinkFetched(AttachmentConvertor.fromLinkInfo(link));
    },
    onError() {
      toast(t('unableToRetrieveLinkInfo'), 'error');
    },
  });

  useEffect(() => {
    if (props.link) {
      reset({ url: props.link });
    }
  }, [props.link, reset]);

  const registerUrl = register('url');
  const watchedUrl = watch('url');

  return (
    <form
      onSubmit={handleSubmit(({ url }) =>
        linkInfo.mutate({ pageId: props.pageId, url: prependHttp(url) }),
      )}
    >
      <Field.Group>
        {errors.url?.message && <Field.Error>{errors.url.message}</Field.Error>}
        <div
          className="tw-flex tw-gap-2"
          data-name={`${props.dataNamePrefix}_add-link`}
        >
          <div className="tw-flex-1">
            <TextInput
              disabled={!!props.onLinkRemove}
              {...registerUrl}
              onPaste={(e) => {
                e.preventDefault();
                setValue('url', prependHttp(e.clipboardData.getData('text')));
              }}
              onBlur={(e) => {
                registerUrl.onBlur(e);
                if (e.target.value) {
                  setValue('url', prependHttp(e.target.value));
                }
              }}
              placeholder={`${t('insertYourLink')} ...`}
              iconAfter={
                props.onLinkRemove && (
                  <IconButton
                    variant="plain"
                    onClick={() => {
                      props.onLinkRemove?.();
                      reset({ url: '' });
                    }}
                  >
                    <Icon icon={faTrash} />
                  </IconButton>
                )
              }
            />
          </div>
          {!props.link && (
            <Button
              type="submit"
              className="tw-shrink-0"
              isLoading={linkInfo.isLoading}
              disabled={!watchedUrl}
              data-name={`${props.dataNamePrefix}-set-link`}
            >
              {t('setLink')}
            </Button>
          )}
        </div>
      </Field.Group>
    </form>
  );
};

export default AttachLink;
